/**
 * keyBindings.ts
 * Configurable keyboard → action mapping for the web build.
 *
 * Each action maps to an array of KeyboardEvent.code values (e.g. 'KeyA').
 * Multiple codes per action allow simultaneous alternatives (WASD + arrows).
 *
 * `capturingKey` is set true by KeyBindingOverlay while waiting for a key
 * press during remapping — KeyboardInput reads it to suspend normal input.
 *
 * Phase 1 — Movement Prototype.
 *
 * @module keyBindings
 */

/** The in-game actions mappable to keyboard keys. */
export type KeyAction = 'left' | 'right' | 'jump' | 'attack' | 'down' | 'up';

/** Maps each action to one or more KeyboardEvent.code strings. */
export interface KeyBindings {
  left: string[];
  right: string[];
  down: string[];
  up: string[];
  jump: string[];
  attack: string[];
}

/** Factory defaults used by `resetBindings()`. */
export const DEFAULT_BINDINGS: Readonly<KeyBindings> = {
  left:   ['KeyA', 'ArrowLeft'],
  right:  ['KeyD', 'ArrowRight'],
  down:   ['KeyS', 'ArrowDown'],
  // DECISION: `up` shares keys with `jump` on purpose — platformer reads
  // `jumpPressed`/`jumpHeld` and ignores `up`; shmup reads `up` and never
  // checks `jumpPressed`. KeyboardInput just sets every matching flag.
  up:     ['KeyW', 'ArrowUp'],
  jump:   ['Space', 'ArrowUp', 'KeyW'],
  attack: ['KeyZ', 'KeyJ'],
};

/**
 * The mutable binding singleton.
 * Written by `remapAction()`; read by `KeyboardInput.tsx` on every keydown.
 */
export const keyBindings: KeyBindings = {
  left:   [...DEFAULT_BINDINGS.left],
  right:  [...DEFAULT_BINDINGS.right],
  down:   [...DEFAULT_BINDINGS.down],
  up:     [...DEFAULT_BINDINGS.up],
  jump:   [...DEFAULT_BINDINGS.jump],
  attack: [...DEFAULT_BINDINGS.attack],
};

/**
 * Flag: true while `KeyBindingOverlay` is capturing a key press for remapping.
 * `KeyboardInput` suspends normal input processing when this is true.
 * Written by `setCapturingKey()`; read by `KeyboardInput.ts`.
 */
export let capturingKey = false;

/**
 * Set or clear the key-capture lock.
 * @param value - true to start capturing, false to release.
 */
export function setCapturingKey(value: boolean): void {
  capturingKey = value;
}

/**
 * Replace the key codes for one action.
 * Strips the new codes from every other action first to prevent a single key
 * from triggering two actions simultaneously (e.g. remapping right → ArrowLeft
 * while left still has ArrowLeft bound would make both fire from one press).
 *
 * DECISION: `up` is intentionally exempt from both sides of the strip pass.
 * It is not user-remappable (KeyBindingOverlay.ACTIONS deliberately omits it)
 * and it must keep its default `KeyW` / `ArrowUp` overlap with `jump` so the
 * same physical key drives platformer jumping and shmup ascent. If a player
 * remaps `jump` to include `KeyW` we still want `up` to retain `KeyW`; the
 * scene-level read split (`jumpPressed`/`jumpHeld` vs `up`) prevents any
 * cross-action firing in practice.
 *
 * @param action - Which action to remap.
 * @param codes  - New list of KeyboardEvent.code strings for this action.
 */
export function remapAction(action: KeyAction, codes: string[]): void {
  const codeSet = new Set(codes);
  for (const other of Object.keys(keyBindings) as KeyAction[]) {
    if (other === action) continue;
    if (other === 'up' || action === 'up') continue;
    keyBindings[other] = keyBindings[other].filter((c) => !codeSet.has(c));
  }
  keyBindings[action] = codes;
}

/**
 * Reset all bindings to factory defaults.
 */
export function resetBindings(): void {
  keyBindings.left   = [...DEFAULT_BINDINGS.left];
  keyBindings.right  = [...DEFAULT_BINDINGS.right];
  keyBindings.down   = [...DEFAULT_BINDINGS.down];
  keyBindings.up     = [...DEFAULT_BINDINGS.up];
  keyBindings.jump   = [...DEFAULT_BINDINGS.jump];
  keyBindings.attack = [...DEFAULT_BINDINGS.attack];
}

/**
 * Convert a KeyboardEvent.code to a short human-readable label.
 * @param code - e.g. 'ArrowLeft', 'KeyA', 'Space'
 * @returns e.g. '←', 'A', 'Space'
 */
export function codeToLabel(code: string): string {
  const MAP: Record<string, string> = {
    ArrowLeft:  '←',
    ArrowRight: '→',
    ArrowUp:    '↑',
    ArrowDown:  '↓',
    Space:      'Space',
    Enter:      'Enter',
    Escape:     'Esc',
    ShiftLeft:  'L-Shift',
    ShiftRight: 'R-Shift',
    ControlLeft: 'L-Ctrl',
  };
  if (MAP[code]) return MAP[code];
  // KeyA → A, KeyZ → Z, Digit1 → 1
  if (/^Key[A-Z]$/.test(code)) return code[3];
  if (/^Digit\d$/.test(code)) return code[5];
  return code;
}
